import { useQuery, useQueryClient } from "@tanstack/react-query";

import { customAxios } from "lib/axios";
import { Organization } from "types";
import { refetchUserOnError } from "utils/refetchUserOnError";

export type PaginationData = {
  page: number;
  limit: number;
  total: number;
  pages: number;
  hasMore: boolean;
};

export type OrgMember = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type PaginatedOrganization = Organization & {
  pagination?: PaginationData;
};

const getOrgMembers = async (
  orgId: string | undefined
): Promise<OrgMember[]> => {
  if (!orgId) {
    return Promise.reject(new Error("Invalid orgId"));
  }

  const { data } = await customAxios.get<{ members: OrgMember[] }>(
    `/organizations/${orgId}/members`
  );

  return data.members;
};

export const useGetAllOrgMembers = (orgId: string | undefined) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["orgMembers", orgId],
    queryFn: () => getOrgMembers(orgId),
    enabled: !!orgId, // prevents unnecessary calls
    onError: (err) => {
      refetchUserOnError(err, queryClient);
    },
    staleTime: 5 * 60 * 1000, // cache for 5 mins (dropdown friendly)
  });
};
